import type { AppLocale } from './messages';

/**
 * Числа в строках интерфейса: «Позвонить 21 участнику?», а не «Позвонить 21 участникам?».
 *
 * <p>Формы пишутся прямо в строке, в синтаксисе ICU — `{count, plural, one {…} few {…} other {…}}`,
 * `#` внутри формы заменяется числом. Так строка остаётся одним ключом во всех четырёх словарях,
 * а переводчик видит все формы рядом. Какую форму взять, решает `Intl.PluralRules` языка: у
 * русского и сербского их по три-четыре (1, 2–4, 5–20, 21…), у английского и французского две.
 *
 * <p>Строка без `plural` тоже годится: `{count}` просто заменяется числом — так старые строки и
 * подмены в тестах работают без правок.
 *
 * <p>Неизвестное число (`null`) — форма `other` без числа: «Позвонить участникам?».
 */

type Category = Intl.LDMLPluralRule;

/**
 * Правила CLDR для целых чисел наших четырёх языков — на случай движка без `Intl.PluralRules`
 * (Hermes собирается и без него). Совпадение с `Intl` проверяет тест на 0…300.
 */
export function builtinPluralCategory(locale: AppLocale, n: number): Category {
  const i = Math.abs(Math.trunc(n));
  if (!Number.isInteger(n)) return 'other';
  const mod10 = i % 10;
  const mod100 = i % 100;
  switch (locale) {
    case 'ru':
      if (mod10 === 1 && mod100 !== 11) return 'one';
      if (mod10 >= 2 && mod10 <= 4 && (mod100 < 12 || mod100 > 14)) return 'few';
      return 'many';
    case 'sr':
      if (mod10 === 1 && mod100 !== 11) return 'one';
      if (mod10 >= 2 && mod10 <= 4 && (mod100 < 12 || mod100 > 14)) return 'few';
      return 'other';
    case 'fr':
      if (i === 0 || i === 1) return 'one';
      // CLDR: «many» — миллионы ровно (1 000 000, 2 000 000…); в интерфейсе таких чисел нет.
      if (i !== 0 && i % 1_000_000 === 0) return 'many';
      return 'other';
    default:
      return i === 1 ? 'one' : 'other';
  }
}

const rulesCache = new Map<AppLocale, Intl.PluralRules | null>();

function intlRules(locale: AppLocale): Intl.PluralRules | null {
  if (!rulesCache.has(locale)) {
    let r: Intl.PluralRules | null;
    try {
      r = typeof Intl !== 'undefined' && typeof Intl.PluralRules === 'function' ? new Intl.PluralRules(locale) : null;
    } catch {
      r = null;
    }
    rulesCache.set(locale, r);
  }
  return rulesCache.get(locale) ?? null;
}

/** Категория числа по правилам языка: `one`, `few`, `many`, `other`… */
export function pluralCategory(locale: AppLocale, n: number): Category {
  return intlRules(locale)?.select(n) ?? builtinPluralCategory(locale, n);
}

/** Разобрать `one {…} few {…}` в словарь форм; вложенные скобки внутри формы допустимы. */
function parseForms(body: string): Map<string, string> {
  const forms = new Map<string, string>();
  let i = 0;
  while (i < body.length) {
    while (i < body.length && /\s/.test(body[i])) i += 1;
    const nameStart = i;
    while (i < body.length && /[a-z=0-9]/i.test(body[i])) i += 1;
    const name = body.slice(nameStart, i);
    while (i < body.length && /\s/.test(body[i])) i += 1;
    if (!name || body[i] !== '{') break;
    let depth = 0;
    const formStart = i + 1;
    for (; i < body.length; i += 1) {
      if (body[i] === '{') depth += 1;
      else if (body[i] === '}') {
        depth -= 1;
        if (depth === 0) break;
      }
    }
    forms.set(name, body.slice(formStart, i));
    i += 1;
  }
  return forms;
}

/**
 * Подставить число в строку с учётом форм множественного числа.
 *
 * @param template строка словаря
 * @param name имя переменной в строке (`count`, `days`)
 */
export function formatCount(template: string, locale: AppLocale, name: string, n: number | null): string {
  const head = `{${name}, plural,`;
  let out = '';
  let rest = template;
  for (;;) {
    const at = rest.indexOf(head);
    if (at < 0) break;
    out += rest.slice(0, at);
    // Конец конструкции — парная закрывающая скобка.
    let depth = 0;
    let end = at;
    for (; end < rest.length; end += 1) {
      if (rest[end] === '{') depth += 1;
      else if (rest[end] === '}') {
        depth -= 1;
        if (depth === 0) break;
      }
    }
    const forms = parseForms(rest.slice(at + head.length, end));
    const exact = n == null ? undefined : forms.get(`=${n}`);
    const form = exact ?? forms.get(n == null ? 'other' : pluralCategory(locale, n)) ?? forms.get('other') ?? '';
    out += form.replace(/#/g, n == null ? '' : String(n));
    rest = rest.slice(end + 1);
  }
  out += rest;
  out = out.split(`{${name}}`).join(n == null ? '' : String(n));
  // Без числа на его месте остаётся двойной пробел. Пробел перед «?» не трогаем: во французском
  // он обязателен.
  return n == null ? out.replace(/\s{2,}/g, ' ').trim() : out;
}
